import { Component, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { CardComponent } from '../../../components/card/card.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { catchError, lastValueFrom, map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { BackButtonComponent } from '../../../components/back-button/back-button.component';
import { LogoImagesComponent } from '../../../components/logo-images/logo-images.component';
import { PasswordToggleComponent } from '../../../components/password-toggle/password-toggle.component';
import { MatSelectModule } from '@angular/material/select';
import { toSignal } from '@angular/core/rxjs-interop';
import { RegistrationEducator } from '../../../interfaces/educator/IRegistrationEducator';
import { ErrorResponse } from '../../../interfaces/general/IErrorResponse';

interface RegistrationEducatorsState {
  educators: RegistrationEducator[];
  isLoading: boolean;
  error: string | null;
}

const STALE_EDUCATOR_ERROR_MESSAGE =
  'De geselecteerde opleider is niet meer beschikbaar. Selecteer opnieuw een opleider.';

@Component({
  selector: 'app-register',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    RouterModule,
    CardComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    CommonModule,
    BackButtonComponent,
    LogoImagesComponent,
    PasswordToggleComponent,
    MatSelectModule
  ]
})
export class RegisterPage {
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly reloadRegistrationEducators = new Subject<void>();

  readonly registerForm: FormGroup<{
    username: FormControl<string | null>;
    email: FormControl<string | null>;
    password: FormControl<string | null>;
    educatorId: FormControl<number | null>;
  }> = this.formBuilder.group({
    username: ['', [this.authService.usernameValidator]],
    email: ['', [Validators.required, Validators.email]],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(6),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/)
      ]
    ],
    educatorId: new FormControl<number | null>({ value: null, disabled: true }, Validators.required)
  });

  readonly registrationEducatorsState = toSignal(
    this.reloadRegistrationEducators.pipe(
      startWith(undefined),
      switchMap(() =>
        this.authService.getRegistrationEducators().pipe(
          map(
            educators =>
              ({
                educators,
                isLoading: false,
                error: null
              }) satisfies RegistrationEducatorsState
          ),
          catchError(() =>
            of({
              educators: [],
              isLoading: false,
              error: 'Opleiders konden niet worden geladen. Probeer het later opnieuw.'
            } satisfies RegistrationEducatorsState)
          ),
          startWith({ educators: [], isLoading: true, error: null } satisfies RegistrationEducatorsState)
        )
      ),
      tap(state => this.setEducatorControlAvailability(state))
    ),
    { initialValue: { educators: [], isLoading: true, error: null } }
  );
  readonly canRegister = computed(() => {
    const state = this.registrationEducatorsState();
    return !state.isLoading && state.error === null && state.educators.length > 0;
  });

  registerError: string | null = null;
  isLoading: boolean = false;
  isSuccesfull: boolean = false;
  readonly registrationSubmitted = signal(false);

  retryRegistrationEducators(): void {
    this.reloadRegistrationEducators.next();
  }

  onEducatorSelected(): void {
    if (this.registerError === STALE_EDUCATOR_ERROR_MESSAGE) {
      this.registerError = null;
    }
  }

  async onRegister(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.registrationSubmitted.set(true);
    if (this.registerForm.invalid || !this.canRegister()) {
      return;
    }

    const { username, email, password, educatorId } = this.registerForm.getRawValue();
    if (educatorId === null) {
      return;
    }

    this.isSuccesfull = false;
    this.isLoading = true;
    this.registerError = null;
    this.registerForm.disable();

    try {
      await lastValueFrom(
        this.authService.register({
          username: username ?? '',
          emailAddress: email ?? '',
          password: password ?? '',
          educatorId
        })
      );

      this.isSuccesfull = true;
    } catch (error: unknown) {
      if (this.isStaleEducatorError(error)) {
        this.registerForm.controls.educatorId.reset(null, { emitEvent: false });
        this.registerError = STALE_EDUCATOR_ERROR_MESSAGE;
        this.retryRegistrationEducators();
      } else {
        this.registerError = 'Er is iets misgegaan tijdens het registreren.';
      }
      this.isSuccesfull = false;
    } finally {
      this.isLoading = false;
      this.registerForm.enable();
      this.setEducatorControlAvailability(this.registrationEducatorsState());
    }
  }

  private isStaleEducatorError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 400 &&
      this.isErrorResponse(error.error) &&
      error.error.message === 'Educator not found'
    );
  }

  private isErrorResponse(error: unknown): error is ErrorResponse {
    return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';
  }

  private setEducatorControlAvailability(state: RegistrationEducatorsState): void {
    const educatorControl = this.registerForm.controls.educatorId;
    if (!state.isLoading && state.error === null && state.educators.length > 0) {
      educatorControl.enable({ emitEvent: false });
      return;
    }

    educatorControl.disable({ emitEvent: false });
  }

  get hasNumber(): boolean {
    const password: string = this.registerForm.get('password')?.value || '';
    return /[0-9]/.test(password);
  }

  get hasUppercase(): boolean {
    const password: string = this.registerForm.get('password')?.value || '';
    return /[A-Z]/.test(password);
  }

  get hasSpecialChar(): boolean {
    const password: string = this.registerForm.get('password')?.value || '';
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  }
}
